import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Popover,
  Paper
} from '@mui/material';
import { saveView, setFilteredCostCenterList, setFilteredSiteList } from '../../ducks/actions';
import { getSaveLoading, getCurrentView, getSaveViewApiUrl, getAllFilteredLists, getCurrentViewAllDefaultFilteredLists, getViews, getViewsInfo } from '../../ducks/selectors';
import { setCommonAlerts } from 'modules/cbre-flow-Common/actions';
import { SYSTEM_DEFAULT_VIEW_NAME } from 'modules/cbre-flow-view-management/ducks/constants';

interface SaveViewAsContentProps {
  open: boolean;
  onClose: () => void;
  isPopover?: boolean;
  anchorEl?: HTMLElement | null;
  selectedClient: any;
  anchorOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

const SaveViewAsContent: React.FC<SaveViewAsContentProps> = ({
  open,
  onClose,
  isPopover = false,
  anchorEl,
  selectedClient,
  anchorOrigin = { vertical: 'top', horizontal: 'center' },
  transformOrigin = { vertical: 'bottom', horizontal: 'center' }
}) => {
  const dispatch = useDispatch();
  const saveLoading = useSelector(getSaveLoading);
  const currentView = useSelector(getCurrentView);

  const [viewName, setViewName] = useState('');
  const maxLength = 36;
  const textFieldRef = useRef<HTMLInputElement>(null);
  // const apiUrl = ''; // Replace with actual API URL if needed
  const saveViewApiUrl = useSelector(getSaveViewApiUrl);
  const allFilteredLists = useSelector(getAllFilteredLists)
  const getAllViews = useSelector(getViews)
  const viewInfo = useSelector(getViewsInfo);


  useEffect(() => {
    if (open) {
      // Focus the text field when dialog opens
      setViewName('')
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleSaveAndApply = async () => {
    if (viewName.trim()) {

      if(viewName.toLowerCase()?.includes(SYSTEM_DEFAULT_VIEW_NAME.toLowerCase())){
        dispatch(setCommonAlerts({ message: `You cannot save a view as "${SYSTEM_DEFAULT_VIEW_NAME}". Please choose a different name.`, type: "failure" }));
        return;
      }

      const isViewNameExists = getAllViews.some((view: any) => (view.viewName).toLowerCase() === ((viewInfo?.clientName || '') + ' - ' + viewName.trim()).toLowerCase());
      if (isViewNameExists) {
        // Handle duplicate view name error (you can show a message to the user)
        dispatch(setCommonAlerts({ message: "A view with this name already exists. Please choose a different name.", type: "failure" }));
        return;
      }

      const viewData = {
        viewName: (viewInfo?.clientName || selectedClient.clientName) + ' - ' + viewName.trim(),
        account: viewInfo?.account || viewInfo?.clientCode || selectedClient.clientCode,
        clientCode: viewInfo?.clientCode || selectedClient.clientCode,
        isDefault: true,
        filterSelections: {
          sites: allFilteredLists.sites,
          costCenters: allFilteredLists.costCenters,
        },
      };
      await dispatch(saveView({ apiUrl: `${saveViewApiUrl}clientCode=${viewInfo?.clientCode || selectedClient.clientCode}`, viewData }));
    }
    dispatch(setFilteredSiteList(currentView, allFilteredLists.sites));
    dispatch(setFilteredCostCenterList(currentView, allFilteredLists.costCenters));
    onClose();
  }

  const isValid = viewName.trim().length > 0;

  const contentJSX = (
    <>
      <Box sx={{
        p: 2,
        fontSize: '18px',
        fontWeight: 600,
        color: '#1A1A1A',
        lineHeight: '24px',
        // borderBottom: isPopover ? '1px solid #e0e0e0' : 'none'
      }}>
        Save view as
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <Typography
          variant="body2"
          sx={{ mb: 2, fontSize: '16px', lineHeight: '24px', color: '#1A1A1A' }}
        >
          Save your current filters as a named view for quick access later.
        </Typography>

        <Box sx={{ p: 0 }}>
          <TextField
            fullWidth
            variant="outlined"
            label="View Name"
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
            disabled={saveLoading}
            placeholder=""
            inputRef={textFieldRef}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',

              }
            }}
            InputProps={{
              startAdornment: (
                <Box
                  component="span"
                  sx={{
                    color: '#666',
                    fontSize: '14px',
                    mr: 0.5,
                    userSelect: 'none',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {viewInfo?.clientName}{' - '}
                </Box>
              ),
            }}
            inputProps={{
              maxLength: maxLength,
              style: { fontSize: '14px', lineHeight: '30px' }
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'right',
              mt: 0.5,
              fontSize: '12px',
              color: viewName.length > maxLength ? '#d32f2f' : '#888'
            }}
          >
            {viewName.length} / {maxLength}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: 2,
          pb: 3,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          // borderTop: isPopover ? '1px solid #e0e0e0' : 'none'
        }}
      >
        <Button
          onClick={onClose}
          disabled={saveLoading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveAndApply}
          variant="contained"
          disabled={!isValid || saveLoading}
          sx={{ textTransform: "none" }}
        >
          {saveLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Saving...
            </Box>
          ) : (
            'Save & apply'
          )}
        </Button>
      </Box>
    </>
  );

  if (isPopover) {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '8px',
              width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
              maxWidth: '500px',
              border: 'none',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              overflow: 'visible'
            }
          }
        }}
      >
        <Box sx={{ position: 'relative', paddingBottom: '12px' }}>
          {/* Triangle */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '0px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              zIndex: 1000,
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: '1px',
                left: '-12px',
                width: 0,
                height: 0,
                borderLeft: '12px solid transparent',
                borderRight: '12px solid transparent',
                borderTop: '12px solid #e0e0e0',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '2px',
                left: '-11px',
                width: 0,
                height: 0,
                borderLeft: '11px solid transparent',
                borderRight: '11px solid transparent',
                borderTop: '11px solid #fff',
              }
            }}
          />

          {/* Content Box */}
          <Box
            sx={{
              borderRadius: '8px',
              width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
              maxWidth: '500px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              border: '1px solid #e0e0e0',
              backgroundColor: '#fff'
            }}
          >
            {contentJSX}
          </Box>
        </Box>
      </Popover>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          width: { xs: 'calc(100vw - 32px)', sm: '100%' },
          maxWidth: '500px'
        }
      }}
    >
      <DialogTitle
        sx={{
          p: 2,
          fontSize: '18px',
          fontWeight: 600,
          color: '#1A1A1A',
          lineHeight: '24px'
        }}
      >
        Save view as
      </DialogTitle>

      <Box sx={{ px: 2, pb: 2 }}>
        <Typography
          variant="body2"
          sx={{ mb: 2, fontSize: '16px', lineHeight: '24px', color: '#1A1A1A' }}
        >
          Save your current filters as a named view for quick access later.
        </Typography>

        <Box sx={{ p: 0 }}>
          <TextField
            fullWidth
            variant="outlined"
            label="View Name"
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
            placeholder=""
            disabled={saveLoading}
            inputRef={textFieldRef}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',

              }
            }}
            InputProps={{
              startAdornment: (
                <Box
                  component="span"
                  sx={{
                    color: '#666',
                    fontSize: '14px',
                    mr: 0.5,
                    userSelect: 'none',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {viewInfo?.clientName}{' - '}
                </Box>
              ),
            }}
            inputProps={{
              maxLength: maxLength,
              style: { fontSize: '14px', lineHeight: '30px' }
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'right',
              mt: 0.5,
              fontSize: '12px',
              color: viewName.length > maxLength ? '#d32f2f' : '#888'
            }}
          >
            {viewName.length} / {maxLength}
          </Typography>
        </Box>
      </Box>

      <DialogActions
        sx={{
          px: 2,
          pb: 3,
          gap: 1,
          justifyContent: 'flex-end'
        }}
      >
        <Button
          onClick={onClose}
          disabled={saveLoading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveAndApply}
          variant="contained"
          disabled={!isValid || saveLoading}
        >
          {saveLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Saving...
            </Box>
          ) : (
            'Save'
          )}
        </Button>
      </DialogActions>
    </Dialog>)
};

export default SaveViewAsContent;